import { useEffect, useState } from "react";
import type { SlotProps } from "../../../core/platform/registry/registry";
import { unresolvedTicketJobs, type TicketPrintJob } from "../ticket-queue";

/**
 * Unresolved tickets in the shift close-out (T022, FR-217).
 *
 * WHY THIS EXISTS. A ticket that failed and was never retried is otherwise
 * invisible once the till moves on — the failure banner (TicketFailureNotice)
 * clears the moment its job is retried or the tab reloads, and nothing else
 * says "three orders never made it to the barista" before the day is closed.
 * Silence here is the same class of problem FR-103 solves for the drawer:
 * a shift can end without anyone learning a printer problem happened.
 *
 * RENDERED VIA A SLOT, NOT IMPORTED BY CORE. `ShiftClose.tsx` renders
 * `<Slot id="till.shiftClose.summary" shiftId={...} />` without knowing this
 * component exists (Principle III) — disabling the module removes this
 * section entirely, per SC-012.
 */

export function TicketCloseoutSummary({ shiftId }: SlotProps) {
  const [jobs, setJobs] = useState<TicketPrintJob[]>([]);

  useEffect(() => {
    let cancelled = false;
    if (typeof shiftId !== "string") return;

    void unresolvedTicketJobs(shiftId).then((rows) => {
      if (!cancelled) setJobs(rows);
    });

    return () => {
      cancelled = true;
    };
  }, [shiftId]);

  if (jobs.length === 0) return null;

  return (
    <section className="ticketcloseout" aria-label="Tickets that never printed">
      <p className="ticketcloseout__title">
        {jobs.length} ticket{jobs.length === 1 ? "" : "s"} never printed this shift
      </p>
      <ul className="ticketcloseout__list">
        {jobs.map((job) => (
          <li key={job.opId}>
            Order {job.document.orderNumber || "—"} ({job.state})
          </li>
        ))}
      </ul>
    </section>
  );
}

export default TicketCloseoutSummary;
